import { FormulationChip } from '@montugroup/circuit-shared';
import type {
  Price,
  ProductFormulation,
  ProductStrain,
  ProductStrainKey,
} from '@montugroup/circuit-shared/types/index';
import { Box, Card, CardContent, Checkbox, Link, Tooltip, Typography, visuallyHidden } from '@montugroup/design-system';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import ConcessionChip from '../components/ConcessionChip/ConcessionChip';
import KeyValueChip from '../components/KeyValueChip/KeyValueChip';
import StrainChip from '../components/StrainChip/StrainChip';

import type { ProductCartEventInfo } from '@/hooks/products/useCartEventInfo';
import { useProductDetailsPath } from '@/hooks/products/useProductDetailsPath';
import { useProductQuantity } from '@/hooks/products/useProductQuantity';
import type { IntervalDetails } from '@montugroup/circuit-shared/types/refill';
import { DispenseIntervalAlert } from './DispenseIntervalAlert';
import RefillDoseOption from './RefillDoseOption';
import { formatPrice } from './util';

export interface ProductInfo {
  productName: string;
  id: number;
  supplier?: string;
  formulation: ProductFormulation;
  strainName: ProductStrain | null;
  strainKey: ProductStrainKey | null;
  thcForDisplay: string;
  cbdForDisplay: string;
  price: Price;
  size: string | null;
  isConcession: boolean;
  intervalDetails?: IntervalDetails;
  prescriptionCode?: string;
}

export interface PrescriptionInfo {
  maxRepeats: number;
  expiry: string;
  maxQuantity: number;
  interval: number;
  productSchedule?: {
    id: number;
    name: string;
  } | null;
}

export interface ProductRefillCardProps {
  product: ProductInfo;
  prescription: PrescriptionInfo;
  cartEventInfo?: ProductCartEventInfo;
  onlyMaxQuantity?: boolean;
  hideRepeats?: boolean;
  showDispenseInterval?: boolean;
  reorderDate?: string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  border: '2px solid',
  borderRadius: theme.shape.borderRadius,
  borderColor: theme.palette.action.disabledBackground,
  marginBottom: theme.spacing(3),
}));

const TooltipBreak = styled(Box)`
  margin-top: 1rem;
`;

const CardLayout = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  '&:last-child': {
    paddingBottom: theme.spacing(4),
  },
}));

const CardHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const CardHeaderTitles = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  align-items: flex-start;
`;

const CardHeaderControls = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ChipsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(2),
    '& > *': {
      marginBottom: 0,
    },
  },
}));

const CardChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const CardBodyScriptDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const DosageOptions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const CardDetails = styled(Box)(() => ({
  overflow: 'hidden',
}));

const CardDetailsInner = styled(Box)(() => ({
  marginTop: '-100%',
  transition: 'all 0.3s',
  ['&.expanded']: {
    marginTop: 0,
  },
}));

const CardSubOptions = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export function ProductRefillCard(props: ProductRefillCardProps) {
  const { prescription, product, cartEventInfo, onlyMaxQuantity, hideRepeats, showDispenseInterval, reorderDate } =
    props;

  const { quantity, setQuantity } = useProductQuantity(product.id, cartEventInfo);
  const { path } = useProductDetailsPath();
  const [openTooltip, setOpenTooltip] = useState(false);

  const handleTooltipToggle = () => {
    setOpenTooltip((prev) => !prev);
  };

  const remainingUnits = product.intervalDetails?.estimatedRemainingUnits;
  const isExceedingRemainingUnits = (quantity: number, remainingUnits?: number): boolean =>
    typeof remainingUnits === 'number' && quantity > remainingUnits;
  const getMaxAllowedQuantity = (prescriptionMaxQuantity: number, remainingUnits?: number): number => {
    if (typeof remainingUnits === 'number' && remainingUnits < prescriptionMaxQuantity) {
      return remainingUnits;
    }
    return prescriptionMaxQuantity;
  };

  // Handle API updates to remainingUnits
  useEffect(() => {
    if (quantity > 0 && isExceedingRemainingUnits(quantity, remainingUnits)) {
      const maxAllowed = getMaxAllowedQuantity(prescription.maxQuantity, remainingUnits);
      setQuantity(maxAllowed);
    }
  }, [remainingUnits, quantity, prescription.maxQuantity, setQuantity]);

  const onCheckboxChanged = useCallback(
    (_: React.ChangeEvent, checked: boolean) => {
      if (checked) {
        const maxAllowed = getMaxAllowedQuantity(prescription.maxQuantity, remainingUnits);
        setQuantity(maxAllowed);
      } else {
        setQuantity(0);
      }
    },
    [prescription.maxQuantity, remainingUnits, setQuantity],
  );

  // Generate an array of integers counting down from maxQuantity to 1;
  const displayedDosageAmounts = onlyMaxQuantity
    ? [prescription.maxQuantity]
    : new Array(prescription.maxQuantity)
        .fill('')
        .map((_, index) => index + 1)
        .reverse();

  return (
    <StyledCard
      elevation={0}
      role="form"
      aria-label={`Product Card for ${product.productName}`}
      data-testid="product-card"
    >
      <CardLayout>
        <CardHeader>
          <CardHeaderTitles>
            <Link
              aria-label="Product Details"
              variant="subtitle2"
              component={RouterLink}
              to={`${path}${product.id}`}
              sx={{ fontWeight: 600 }}
            >
              {product.productName}
            </Link>
            <Typography variant="body2" aria-label="Supplier">
              {product.supplier}
            </Typography>
            <Typography variant="body2">
              {product.size ? <span aria-label="Size">{product.size}</span> : null}
              {product.size ? <span aria-hidden>&nbsp;|&nbsp;</span> : null}
              <span>
                RRP&nbsp;
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {formatPrice(product.price)}
                </Typography>
              </span>
            </Typography>
          </CardHeaderTitles>
          <CardHeaderControls>
            <Checkbox
              id={`product-checkbox-${product.id}`}
              data-tracking-id={`ProductRefillCard_Checkbox_${product.id}`}
              checked={quantity > 0}
              onChange={onCheckboxChanged}
            />
            <label htmlFor={`product-checkbox-${product.id}`} style={visuallyHidden}>
              Order {product.productName}
            </label>
          </CardHeaderControls>
        </CardHeader>
        {showDispenseInterval && (
          <DispenseIntervalAlert productId={product.id} reorderDate={reorderDate} intervalStatus="PRE_ORDER" />
        )}
        <ChipsWrapper>
          <CardChipContainer>
            <FormulationChip formulation={product.formulation} />
            {Boolean(product.strainName) && Boolean(product.strainKey) && (
              <StrainChip
                strainName={product.strainName as ProductStrain}
                strainKey={product.strainKey as ProductStrainKey}
              />
            )}
            {Boolean(product.isConcession) && <ConcessionChip />}
          </CardChipContainer>
          <CardChipContainer>
            {Boolean(product.thcForDisplay) && <KeyValueChip name="THC:" value={product.thcForDisplay} />}
            {Boolean(product.cbdForDisplay) && <KeyValueChip name="CBD:" value={product.cbdForDisplay} />}
          </CardChipContainer>
        </ChipsWrapper>

        <CardDetails>
          <CardDetailsInner className={quantity <= 0 ? '' : 'expanded'}>
            {quantity <= 0 ? null : (
              <>
                <CardBodyScriptDetails>
                  {hideRepeats ? (
                    <Box sx={{ display: 'none' }} />
                  ) : (
                    <Tooltip
                      onMouseOver={handleTooltipToggle}
                      onClose={() => setOpenTooltip(false)}
                      disableHoverListener={false}
                      onClick={handleTooltipToggle}
                      open={openTooltip}
                      leaveTouchDelay={5000}
                      title={
                        <Typography data-testid="repeats-tooltip">
                          <Typography component="span" variant="caption">
                            {`You can only re-order a repeat prescription for your medication after a ${prescription.interval}-day waiting period to prevent over-ordering.`}
                          </Typography>
                          <TooltipBreak>
                            <Typography component="span" variant="caption">
                              <strong>Repeats</strong>: Each repeat contains {prescription.maxQuantity} units
                            </Typography>
                          </TooltipBreak>
                        </Typography>
                      }
                    >
                      <CardSubOptions>
                        <Typography data-testid="repeats-tag" variant="body2">
                          <u>Repeats:</u>
                        </Typography>
                        <Typography variant="body2">
                          <strong>{prescription.maxRepeats}</strong>
                        </Typography>
                      </CardSubOptions>
                    </Tooltip>
                  )}

                  {Boolean(product.prescriptionCode) && (
                    <CardSubOptions>
                      <Typography variant="body2">Script ID:</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {product.prescriptionCode}
                      </Typography>
                    </CardSubOptions>
                  )}

                  {Boolean(prescription.expiry) && (
                    <CardSubOptions>
                      <Typography variant="body2">Script expiry:</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {moment(prescription.expiry).format('MMM DD, YYYY')}
                      </Typography>
                    </CardSubOptions>
                  )}
                </CardBodyScriptDetails>
                <DosageOptions role="group" aria-label="Dosage Amount">
                  {displayedDosageAmounts.map((amount) => {
                    const exceedsRemainingUnits = isExceedingRemainingUnits(amount, remainingUnits);
                    return (
                      <RefillDoseOption
                        key={`${amount}/${prescription.maxQuantity}`}
                        quantity={amount}
                        setQuantity={setQuantity}
                        maxQuantity={prescription.maxQuantity}
                        singleOption={onlyMaxQuantity}
                        prescriptionPeriodDays={prescription.interval}
                        pricePerUnit={product.price}
                        isSelected={amount === quantity}
                        isDisabled={exceedsRemainingUnits}
                      />
                    );
                  })}
                </DosageOptions>
              </>
            )}
          </CardDetailsInner>
        </CardDetails>
      </CardLayout>
    </StyledCard>
  );
}

export default ProductRefillCard;
